import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { CircularProgress, Dialog, Stack } from "@mui/material";
import { useAgenticLogicRule } from "@src/api/agentic-logic/get-agentic-logic-rule";
import { useAgenticLogicRuleScenarios } from "@src/api/agentic-logic/get-agentic-logic-rule-scenarios";
import { ProductPicker } from "@src/components/MainApp/SharedProductPicker/ProductPicker";
import {
  ShopifyItemPicker,
  ShopifyItemPickerItem,
} from "@src/components/MainApp/ShopifyItemPicker/ShopifyItemPicker";
import { useShopifyPickerItems } from "@src/components/MainApp/ShopifyItemPicker/useShopifyPickerItems";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import {
  SurveyCardFooterLink,
  SurveyCardShell,
  SurveyNumberedOption,
  SurveyOptionList,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/SurveyCardParts";
import { useDispatch, useSelector } from "@src/store";
import { useReportRailComposerCard } from "./railComposerCard";
import {
  closeProductPicker,
  getProductPickerProducts,
  getProductPickerSelectedProducts,
  openProductPicker,
} from "@src/store/slices/productPicker";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";
import { useImmer } from "use-immer";
import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "motion/react";
import { graphConditionsOnDelivery } from "../graph/conditionSubject";
import { useRuleSelectionGraph } from "../graph/useRuleSelectionGraph";
import { useActiveAgenticLogicView } from "../surface/useActiveAgenticLogicView";
import {
  CART_DELIVERY_MODIFIERS,
  CartDeliveryModifier,
  CartPickItem,
  CartPickKind,
  formatCartPickReply,
  formatSuggestedCartRun,
  MAX_CART_PICK_LINES,
  toCartPickLines,
} from "./cartPickReply";

const ASK =
  "Time to test the rule — which cart should I run the steps against?";

const PRODUCT_PICKER_TYPE = "agenticLogicCartTest";

/**
 * The rows the card opens on: a cart built from whatever the merchant picks
 * out of the catalog. Copy is the Figma's.
 */
const GENERIC_ROWS: { kind: CartPickKind; label: string }[] = [
  { kind: "products", label: "Specific products" },
  { kind: "collection", label: "Products from a collection" },
  { kind: "tag", label: "Products with a tag" },
];

const CATALOG_LABEL = "Pick your own cart";
const BACK_LABEL = "Back to suggested carts";

const ITEM_PICKERS: Record<
  Exclude<CartPickKind, "products">,
  {
    title: string;
    searchPlaceholder: string;
    itemNoun: { singular: string; plural: string };
  }
> = {
  collection: {
    title: "Select a collection",
    searchPlaceholder: "Search collections",
    itemNoun: { singular: "collection", plural: "collections" },
  },
  tag: {
    title: "Select product tags",
    searchPlaceholder: "Search product tags",
    itemNoun: { singular: "tag", plural: "tags" },
  },
};

/**
 * The carts Atlas can run, offered above the composer.
 *
 * Testing a rule is a conversation, not a control on the canvas: picking a
 * cart here sends an ordinary user message, and Atlas answers it by running
 * the cart through its own tool. That keeps one path to a run — the graph has
 * no picker of its own — and lets the same request be typed instead ("try a
 * blue bikini XXL plus a mug"), which no chip row could express.
 *
 * Two kinds of cart are on offer. The rule's own example carts are the list:
 * generated for its branches so each one exercises a step, they are the carts
 * worth running first, and a merchant who has just watched the rule get built
 * has no cart of their own in mind yet. Behind a last row, "Pick your own
 * cart", are the three catalog rows — specific products, a collection, or a
 * tag — for the merchant who does. Those open a picker, and Done sends the
 * pick as ids for the agent to resolve — the frontend never assembles a cart
 * line, which needs a price and the product's tags and collections. Specific
 * products open the shared picker the way the app's other cart tester does,
 * with variants: every checked variant is a line, so a merchant testing a
 * size or colour branch picks that size or colour rather than whatever
 * variant the agent reaches first.
 * A rule whose example carts have not been generated has only the catalog
 * rows, shown outright with no way back to a list that does not exist. Only
 * one list is on screen at a time, so a way out is never a fourth row under
 * carts the merchant did not ask for.
 *
 * The card is rendered locally rather than parked by the agent, so it is
 * already there when the author lands on the tab: offering it costs no turn,
 * and nothing is preselected. The agent's own question always outranks it —
 * whenever one is parked, this steps aside so the composer shows a single ask.
 * The picker dialogs are rendered outside that guard: a stream starting while
 * a merchant is mid-pick must not close the dialog under them.
 *
 * Under either list sits a Delivery row of toggle chips, but only for a rule
 * that branches on the checkout delivery. Such a rule (pickup, a rate,
 * transit days) cannot be lit by a cart alone, so a chosen modifier rides
 * along on whichever cart is picked next as a clause the cart-run prompt
 * turns into the delivery block. A rule that never reads the delivery is not
 * asked about it: the row would be a question with no bearing on the run. A
 * modifier on its own is not a run: it changes nothing until a cart is chosen.
 */
export const CartScenarioAsk = () => {
  const dispatch = useDispatch();
  // This ask renders inside the rail, which lives outside the page tree, so
  // the surface comes from the registry.
  const { view, tab } = useActiveAgenticLogicView();
  const ruleId = view.kind === "rule" ? view.ruleId : null;
  const isOnSelectionLogic = ruleId !== null && tab === "selection_logic";

  const { inputRequirement, submitInput, disabled, streaming } =
    useExecutionPanelContext();
  const reducedMotion = useReducedMotion();
  const [cartReady, setCartReady] = useImmer(true);
  const wasStreaming = useRef(false);
  useEffect(() => {
    if (streaming) {
      wasStreaming.current = true;
      setCartReady(false);
      return;
    }
    if (!wasStreaming.current) {
      return;
    }
    wasStreaming.current = false;
    if (reducedMotion) {
      setCartReady(true);
      return;
    }
    const timer = window.setTimeout(() => setCartReady(true), 200);
    return () => window.clearTimeout(timer);
  }, [streaming, setCartReady, reducedMotion]);
  const scenariosQuery = useAgenticLogicRuleScenarios(ruleId ?? undefined);
  // The message names the rule, so the merchant reads a sentence about their
  // own rule rather than a uuid — and so a pick still means the rule they are
  // LOOKING at when the rail is still showing an earlier rule's conversation.
  const ruleQuery = useAgenticLogicRule(ruleId ?? undefined);
  const ruleName = ruleQuery.data?.rule.name ?? null;
  // Whether the rule reads the checkout delivery at all decides whether the
  // Delivery row is offered — the graph is what says so.
  const offersDelivery = graphConditionsOnDelivery(
    useRuleSelectionGraph(ruleId ?? undefined).graph
  );

  const productPickerOpen = useSelector(
    isProductPickerOpen(PRODUCT_PICKER_TYPE)
  );
  const selectedProducts = useSelector(getProductPickerSelectedProducts);
  const loadedProducts = useSelector(getProductPickerProducts);
  // Which item picker (collection / tag) is open; the product picker's open
  // state is the store's, keyed by type so the Exclusions tab's cannot collide.
  const [itemPicker, setItemPicker] = useImmer<Exclude<
    CartPickKind,
    "products"
  > | null>(null);

  // Dismissal lasts as long as this visit: the key changes when the rule or
  // the tab does, so leaving Selection logic and coming back offers the carts
  // again without any timer or stored flag. A pick in progress is abandoned
  // with the visit — it was about the rule that is no longer on screen.
  const visitKey = `${ruleId ?? ""}:${tab}`;
  const [dismissedVisitKey, setDismissedVisitKey] = useImmer<string | null>(
    null
  );
  const [lastVisitKey, setLastVisitKey] = useImmer(visitKey);
  // Whether the merchant stepped past the rule's example carts to the catalog
  // rows behind "Pick your own cart". Reset with the visit, so the suggested
  // carts lead again next time — and they lead outright when the set arrives
  // for a rule that had none when the card first rendered.
  const [showingCatalog, setShowingCatalog] = useImmer(false);
  // The delivery pinned to the next cart run; a toggle, so a second click
  // clears it. Abandoned with the visit like a pick in progress, and ignored
  // once a regenerated graph stops reading the delivery — the row is gone,
  // so a pin made under the old graph must not ride along unseen.
  const [pinnedDelivery, setDelivery] = useImmer<CartDeliveryModifier | null>(
    null
  );
  const delivery = offersDelivery ? pinnedDelivery : null;
  if (lastVisitKey !== visitKey) {
    setLastVisitKey(() => visitKey);
    setDismissedVisitKey(() => null);
    setShowingCatalog(() => false);
    setItemPicker(() => null);
    setDelivery(() => null);
    if (productPickerOpen) {
      dispatch(closeProductPicker());
    }
  }

  const showCard =
    isOnSelectionLogic &&
    dismissedVisitKey !== visitKey &&
    // Atlas is mid-thought, or has a question of its own on the composer.
    !streaming &&
    cartReady &&
    !disabled &&
    inputRequirement.userInput.type === "text" &&
    ruleName !== null;
  useReportRailComposerCard(showCard);

  const scenarios = scenariosQuery.data?.scenarios ?? [];
  const isGenerating = scenariosQuery.data?.status === "pending";
  const hasExamples = scenarios.length > 0 || isGenerating;
  // The catalog rows are the only list a rule without example carts has.
  const showingSuggested = hasExamples && !showingCatalog;

  const submitPick = (kind: CartPickKind, items: CartPickItem[]) => {
    // The rule query was cleared under us; a nameless request is not sent.
    if (ruleName === null || items.length === 0) {
      return;
    }
    submitInput(formatCartPickReply({ ruleName, kind, items, delivery }));
  };

  const openRow = (kind: CartPickKind) => {
    if (kind === "products") {
      dispatch(openProductPicker({ type: PRODUCT_PICKER_TYPE }));
    } else {
      setItemPicker(() => kind);
    }
  };

  const cartLines = toCartPickLines(selectedProducts, loadedProducts);
  const tooManyLines = cartLines.length > MAX_CART_PICK_LINES;

  // Figma "Survey card" Select=Single with `Stepper` off (173:42): the cart
  // offer is the same card the agent's own questions render on, so it takes
  // the shared shell, rows and footer rather than a look of its own. The way
  // between the two lists is the card's link footer — forward to the catalog
  // rows, or back (with its arrow) to the suggested carts.
  const card = (
    <SurveyCardShell
      testId="cart-scenario-ask"
      title={ASK}
      disabled={false}
      dismissLabel="Dismiss cart suggestions"
      onDismiss={() => setDismissedVisitKey(() => visitKey)}
    >
      <SurveyOptionList hasDividedFooter={hasExamples}>
        {showingSuggested ? (
          <>
            {scenarios.map((scenario, index) => (
              <SurveyNumberedOption
                key={scenario.name}
                index={index}
                title={scenario.name}
                onClick={() => {
                  // The card only shows with a named rule; the query can
                  // still be cleared under a click in flight.
                  if (ruleName === null) {
                    return;
                  }
                  submitInput({
                    type: "text",
                    content: formatSuggestedCartRun({
                      ruleName,
                      scenarioName: scenario.name,
                      delivery,
                    }),
                  });
                }}
              />
            ))}
            {isGenerating ? (
              <Stack direction="row" gap={2} alignItems="center" sx={{ p: 2 }}>
                <CircularProgress size={14} />
                <IgTypography variant="caption" color="text.secondary">
                  Generating example carts…
                </IgTypography>
              </Stack>
            ) : null}
          </>
        ) : (
          GENERIC_ROWS.map((row, index) => (
            <SurveyNumberedOption
              key={row.kind}
              index={index}
              title={row.label}
              onClick={() => openRow(row.kind)}
            />
          ))
        )}
      </SurveyOptionList>
      {offersDelivery ? (
        <DeliveryRow
          value={delivery}
          onChange={(next) => setDelivery(() => next)}
        />
      ) : null}
      {showingSuggested ? (
        <SurveyCardFooterLink
          label={CATALOG_LABEL}
          onClick={() => setShowingCatalog(() => true)}
        />
      ) : hasExamples ? (
        <SurveyCardFooterLink
          label={BACK_LABEL}
          arrow
          onClick={() => setShowingCatalog(() => false)}
        />
      ) : null}
    </SurveyCardShell>
  );

  return (
    <>
      {showCard ? (
        <motion.div
          initial={reducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : { duration: 0.28, ease: [0, 0, 0.2, 1] }
          }
        >
          {card}
        </motion.div>
      ) : null}
      <Dialog
        disableEscapeKeyDown
        fullWidth
        maxWidth="lg"
        open={productPickerOpen}
        sx={{ zIndex: 1401 }}
        onClose={() => dispatch(closeProductPicker())}
        slotProps={{ paper: { style: { maxHeight: "700px" } } }}
      >
        <ProductPicker
          loadVariants
          dialogActions={[
            ...(tooManyLines
              ? [
                  <IgTypography key="cap" variant="caption" color="error">
                    {`Pick up to ${MAX_CART_PICK_LINES} variants`}
                  </IgTypography>,
                ]
              : []),
            <IgButton
              key="cancel"
              variant="text"
              onClick={() => dispatch(closeProductPicker())}
            >
              Cancel
            </IgButton>,
            <IgButton
              key="done"
              variant="contained"
              disabled={
                cartLines.length === 0 || tooManyLines || streaming || disabled
              }
              onClick={() => {
                submitPick("products", cartLines);
                dispatch(closeProductPicker());
              }}
            >
              Done
            </IgButton>,
          ]}
        />
      </Dialog>
      {itemPicker ? (
        <Dialog
          fullWidth
          maxWidth="md"
          open
          sx={{ zIndex: 1401 }}
          onClose={() => setItemPicker(() => null)}
        >
          <CartPickItemPicker
            kind={itemPicker}
            saveDisabled={streaming || disabled}
            onClose={() => setItemPicker(() => null)}
            onSave={(selected) => {
              submitPick(itemPicker, selected);
              setItemPicker(() => null);
            }}
          />
        </Dialog>
      ) : null}
    </>
  );
};

// The delivery modifier chips under either list. Selecting one pins it to
// the next cart run; clicking it again lets it go.
const DeliveryRow = ({
  value,
  onChange,
}: {
  value: CartDeliveryModifier | null;
  onChange: (next: CartDeliveryModifier | null) => void;
}) => (
  <Stack sx={{ px: 3 }}>
    <IgDivider spacing="s" />
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      role="group"
      aria-label="Delivery"
      data-testid="cart-delivery-modifier"
      sx={{ px: 2, py: 2 }}
    >
      <IgTypography variant="caption" color="text.secondary">
        Delivery
      </IgTypography>
      {CART_DELIVERY_MODIFIERS.map((modifier) => (
        <IgChip
          key={modifier.id}
          clickable
          aria-pressed={value === modifier.id}
          // The theme's accent chip is the tinted, bordered "selected" look;
          // a filled primary chip is the palette's near-black on dark text.
          color={value === modifier.id ? "accent" : "default"}
          label={modifier.label}
          size="small"
          onClick={() => onChange(value === modifier.id ? null : modifier.id)}
        />
      ))}
    </Stack>
  </Stack>
);

// Separate component so the store-wide item list is only fetched while the
// picker dialog is mounted.
const CartPickItemPicker = ({
  kind,
  saveDisabled,
  onClose,
  onSave,
}: {
  kind: Exclude<CartPickKind, "products">;
  saveDisabled: boolean;
  onClose: () => void;
  onSave: (selected: ShopifyItemPickerItem[]) => void;
}) => {
  const config = ITEM_PICKERS[kind];
  const { items, loading } = useShopifyPickerItems(kind);

  return (
    <ShopifyItemPicker
      title={config.title}
      itemNoun={config.itemNoun}
      searchPlaceholder={config.searchPlaceholder}
      saveLabel="Done"
      items={items}
      loading={loading}
      saveDisabled={saveDisabled}
      onClose={onClose}
      onSave={onSave}
    />
  );
};
